/**
 * 공정 흐름도(물질·에너지수지) 화면의 상태와 실행.
 *
 * 계산은 `src/calc/processNetwork.ts` 가 한다 — 여기는 화면 입력(문자열)을
 * 그 계산 모듈의 타입으로 바꾸는 것과 노드 목록 편집만 맡는다.
 *
 * React 를 import 하지 않는다 — 화면 없이 시험할 수 있어야 한다.
 */
import {
  solveProcessNetwork,
  type LiquidStream,
  type ProcessNetworkResult,
  type ProcessNode,
  type WetGasStream,
} from "../calc/processNetwork";
import { moleFractionsToMassComponents } from "../calc/gasComposition";
import { innerDiameterMm } from "../data/pipes";
import { PIPE_ROUGHNESS_M } from "../calc/pipeFlow";
import { num } from "./formBits";
import { ATMOSPHERIC_KPA, convertInput, toKpa, type PressureUnit } from "./pressureUnit";

export type NodeKind =
  | "source"
  | "sink"
  | "mixer"
  | "splitter"
  | "pipe"
  | "heater"
  | "scrubber"
  | "valve"
  | "reductionFurnace"
  | "esfOffgas";

export const NODE_KIND_LABELS: Record<NodeKind, string> = {
  source: "소스 (경계 유입 스트림)",
  sink: "싱크 (경계 유출 지점)",
  mixer: "믹서 (합류)",
  splitter: "스플리터 (분기)",
  pipe: "배관 구간",
  heater: "히터/쿨러 (감열)",
  scrubber: "스크러버 (냉각·응축/증발)",
  valve: "밸브 (고정 ΔP)",
  reductionFurnace: "환원로 (H2 유동/샤프트 환원)",
  esfOffgas: "ESF off-gas (전기용융로 탄소환원, 독립)",
};

export interface GasComponentEntryForm {
  readonly id: string;
  /** `gasComponents.ts` 의 성분 key. */
  readonly key: string;
  readonly molePercent: string;
}

export interface SplitterBranchForm {
  readonly id: string;
  readonly label: string;
  readonly isFraction: boolean;
  readonly value: string;
}

/**
 * 노드 하나의 화면 입력. 모든 종류의 필드를 한 덩어리에 두고, 종류별로
 * 안 쓰는 필드는 그냥 무시한다(이 프로젝트의 다른 폼과 같은 방식).
 */
export interface FlowsheetNodeForm {
  readonly id: string;
  readonly kind: NodeKind;
  readonly label: string;

  /** sink·splitter·pipe·heater·scrubber·valve 의 입력 참조 */
  readonly inputId: string;
  /** mixer 의 입력 참조들 */
  readonly inputIds: readonly string[];

  /* ── source ── */
  readonly phase: "gas" | "liquid";
  /** "average" = 평균값 직접 입력(기존 방식), "components" = 성분 몰% 입력 */
  readonly compositionMode: "average" | "components";
  /** 건가스 총 질량유량 — 두 조성 입력 방식이 공유한다. */
  readonly dryGasMassFlowKgH: string;
  /** compositionMode "average" 전용 — 아래 3개도 마찬가지. */
  readonly dryGasMolecularWeight: string;
  readonly dryGasSpecificHeatJKgK: string;
  readonly viscosityCp: string;
  /** compositionMode "components" 전용. */
  readonly components: readonly GasComponentEntryForm[];
  readonly compressibility: string;
  readonly waterVaporMassFlowKgH: string;
  readonly massFlowKgH: string;
  readonly densityKgM3: string;
  readonly specificHeatJKgK: string;
  readonly temperatureK: string;
  /** source 의 압력 — 사용자 단위 게이지압 */
  readonly pressureGauge: string;

  /* ── 환원로 / ESF (둘이 공유) ── */
  /** 광석 중 Fe2O3 질량유량 (kg/h). */
  readonly oreFe2O3MassFlowKgH: string;
  /** 환원로 = 목표 금속화율, ESF = (재입력하는) 도달 금속화율. */
  readonly metallizationPercent: string;
  /** ESF 전용 — 공급 탄소량. */
  readonly carbonMassFlowKgH: string;

  /* ── pipe ── */
  readonly nps: string;
  readonly schedule: string;
  readonly useCustomId: boolean;
  readonly customIdMm: string;
  readonly straightLengthM: string;
  readonly fittingEquivalentLengthM: string;
  readonly material: keyof typeof PIPE_ROUGHNESS_M;
  readonly fittingK: string;

  /* ── splitter ── */
  readonly branches: readonly SplitterBranchForm[];

  /* ── heater ── */
  readonly dutyKw: string;

  /* ── scrubber ── */
  readonly outletTemperatureK: string;
  /** 사용자 단위 게이지압 */
  readonly outletPressureGauge: string;
  readonly saturationPercent: string;
  readonly makeupWaterMassFlowKgH: string;

  /* ── valve ── */
  readonly pressureDropKpa: string;
}

/** 단위 전환 때 함께 환산해야 하는 압력 입력 (게이지압만 — ΔP는 크기라 그대로 둔다). */
export const FLOWSHEET_GAUGE_FIELDS = [
  "pressureGauge",
  "outletPressureGauge",
] as const satisfies readonly (keyof FlowsheetNodeForm)[];

let counter = 0;
function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}${counter}`;
}

export function newFlowsheetNode(
  kind: NodeKind,
  existing: readonly FlowsheetNodeForm[],
): FlowsheetNodeForm {
  const id = nextId("n");
  const firstOtherId = existing[0]?.id ?? "";
  return {
    id,
    kind,
    label: `${NODE_KIND_LABELS[kind].split(" ")[0]} ${existing.filter((n) => n.kind === kind).length + 1}`,
    inputId: firstOtherId,
    inputIds: firstOtherId ? [firstOtherId] : [],

    phase: "gas",
    compositionMode: "average",
    dryGasMassFlowKgH: "1000",
    dryGasMolecularWeight: "29",
    dryGasSpecificHeatJKgK: "1005",
    viscosityCp: "0.018",
    components: [{ id: nextId("gc"), key: "nitrogen", molePercent: "100" }],
    compressibility: "1",
    waterVaporMassFlowKgH: "0",
    massFlowKgH: "1000",
    densityKgM3: "1000",
    specificHeatJKgK: "4186",
    temperatureK: "320",
    pressureGauge: "0",

    oreFe2O3MassFlowKgH: "1000",
    metallizationPercent: "90",
    carbonMassFlowKgH: "50",

    nps: "6",
    schedule: "40",
    useCustomId: false,
    customIdMm: "150",
    straightLengthM: "20",
    fittingEquivalentLengthM: "5",
    material: "commercialSteel",
    fittingK: "0",

    branches: [
      { id: nextId("b"), label: "가지 1", isFraction: true, value: "0.5" },
      { id: nextId("b"), label: "가지 2", isFraction: true, value: "0.5" },
    ],

    dutyKw: "0",

    outletTemperatureK: "310",
    outletPressureGauge: "0",
    saturationPercent: "100",
    makeupWaterMassFlowKgH: "0",

    pressureDropKpa: "10",
  };
}

export interface FlowsheetState {
  readonly nodes: readonly FlowsheetNodeForm[];
}

export function newFlowsheetState(): FlowsheetState {
  const src = newFlowsheetNode("source", []);
  const snk = newFlowsheetNode("sink", [src]);
  return { nodes: [src, { ...snk, inputId: src.id }] };
}

export function convertFlowsheetPressures(
  s: FlowsheetState,
  from: PressureUnit,
  to: PressureUnit,
): FlowsheetState {
  if (from === to) return s;
  return {
    nodes: s.nodes.map((n) => {
      let next = n;
      for (const key of FLOWSHEET_GAUGE_FIELDS) {
        next = { ...next, [key]: convertInput(n[key], from, to) };
      }
      return next;
    }),
  };
}

/**
 * 이 노드가 밖으로 내놓는 "출력 지점" 목록 — 다른 노드의 입력 드롭다운에
 * 쓴다. 대부분 자기 id 하나, 스플리터는 가지마다, 스크러버는 가스·액체
 * 두 개.
 */
export function outputPointsOf(
  n: FlowsheetNodeForm,
): ReadonlyArray<{ readonly id: string; readonly label: string }> {
  switch (n.kind) {
    case "sink":
      return [];
    case "splitter":
      return n.branches.map((b) => ({ id: b.id, label: `${n.label} · ${b.label}` }));
    case "scrubber":
      return [
        { id: `${n.id}_gas`, label: `${n.label} · 가스 출구` },
        { id: `${n.id}_liq`, label: `${n.label} · 응축/잔여수 출구` },
      ];
    default:
      return [{ id: n.id, label: n.label }];
  }
}

/** 화면 전체 노드에서 참조 가능한 모든 출력 지점(자기 자신 제외). */
export function referenceOptions(
  nodes: readonly FlowsheetNodeForm[],
  excludeId: string,
): ReadonlyArray<{ readonly id: string; readonly label: string }> {
  return nodes
    .filter((n) => n.id !== excludeId)
    .flatMap((n) => outputPointsOf(n));
}

/* ── 계산 ────────────────────────────────────────────────── */

/**
 * "평균값 직접 입력" 소스는 자기 자신을 성분 하나("사용자 정의 평균값")로
 * 표현한다 — 노드마다 다른 값을 가지므로 표준 성분 DB(`gasComponents.ts`)
 * 대신 노드 id 로 key를 만들고 `customComponentDefs` 에 그 자리에서 정의를
 * 실어 보낸다. 비열비 k는 더 이상 별도 입력을 받지 않는다 — 입력한
 * 질량기준 비열과 분자량에서 (몰기준 Cp) → k = Cp/(Cp−R) 로 그때그때
 * 유도된다(이상기체 순물질이면 이 관계가 정확하다).
 */
function gasSourceStream(
  n: FlowsheetNodeForm,
  pressureKpaAbs: number,
): WetGasStream {
  const totalMassFlowKgH = num(n.dryGasMassFlowKgH);
  const base = {
    compressibility: num(n.compressibility),
    waterVaporMassFlowKgH: num(n.waterVaporMassFlowKgH),
    temperatureK: num(n.temperatureK),
    pressureKpaAbs,
  } as const;

  if (n.compositionMode === "components") {
    const components =
      moleFractionsToMassComponents(
        totalMassFlowKgH,
        n.components.map((c) => ({ key: c.key, molePercent: num(c.molePercent) })),
      ) ?? {};
    return { phase: "gas", components, ...base };
  }

  const customKey = `_avg_${n.id}`;
  const molecularWeight = num(n.dryGasMolecularWeight);
  return {
    phase: "gas",
    components: { [customKey]: totalMassFlowKgH },
    customComponentDefs: {
      [customKey]: {
        key: customKey,
        label: "사용자 정의 평균값",
        formula: "",
        molecularWeight,
        idealGasCpJKmolK: num(n.dryGasSpecificHeatJKgK) * molecularWeight,
        viscosityCp: num(n.viscosityCp),
        lelVolPercent: null,
        uelVolPercent: null,
        hhvMjKg: null,
      },
    },
    ...base,
  };
}

function toProcessNode(
  n: FlowsheetNodeForm,
  unit: PressureUnit,
): ProcessNode {
  const gaugeToAbs = (s: string) => toKpa(num(s), unit) + ATMOSPHERIC_KPA;

  switch (n.kind) {
    case "source": {
      const stream: WetGasStream | LiquidStream =
        n.phase === "gas"
          ? gasSourceStream(n, gaugeToAbs(n.pressureGauge))
          : {
              phase: "liquid",
              massFlowKgH: num(n.massFlowKgH),
              densityKgM3: num(n.densityKgM3),
              viscosityCp: num(n.viscosityCp),
              specificHeatJKgK: num(n.specificHeatJKgK),
              temperatureK: num(n.temperatureK),
              pressureKpaAbs: gaugeToAbs(n.pressureGauge),
            };
      return { kind: "source", id: n.id, label: n.label, stream };
    }
    case "sink":
      return { kind: "sink", id: n.id, label: n.label, inputId: n.inputId };
    case "mixer":
      return {
        kind: "mixer",
        id: n.id,
        label: n.label,
        inputIds: n.inputIds,
      };
    case "splitter":
      return {
        kind: "splitter",
        id: n.id,
        label: n.label,
        inputId: n.inputId,
        branches: n.branches.map((b) => ({
          id: b.id,
          label: b.label,
          isFraction: b.isFraction,
          value: num(b.value),
        })),
      };
    case "pipe": {
      const innerDiameterM = n.useCustomId
        ? num(n.customIdMm) / 1000
        : (innerDiameterMm(n.nps, n.schedule) ?? Number.NaN) / 1000;
      return {
        kind: "pipe",
        id: n.id,
        label: n.label,
        inputId: n.inputId,
        innerDiameterM,
        equivalentLengthM: num(n.straightLengthM) + num(n.fittingEquivalentLengthM),
        roughnessM: PIPE_ROUGHNESS_M[n.material],
        fittingK: num(n.fittingK),
      };
    }
    case "heater":
      return {
        kind: "heater",
        id: n.id,
        label: n.label,
        inputId: n.inputId,
        dutyW: num(n.dutyKw) * 1000,
      };
    case "scrubber":
      return {
        kind: "scrubber",
        id: n.id,
        label: n.label,
        inputId: n.inputId,
        outletTemperatureK: num(n.outletTemperatureK),
        outletPressureKpaAbs: gaugeToAbs(n.outletPressureGauge),
        saturationFraction: num(n.saturationPercent) / 100,
        makeupWaterMassFlowKgH: num(n.makeupWaterMassFlowKgH),
        gasOutputId: `${n.id}_gas`,
        liquidOutputId: `${n.id}_liq`,
      };
    case "valve":
      return {
        kind: "valve",
        id: n.id,
        label: n.label,
        inputId: n.inputId,
        pressureDropKpa: num(n.pressureDropKpa),
      };
    case "reductionFurnace":
      return {
        kind: "reductionFurnace",
        id: n.id,
        label: n.label,
        inputId: n.inputId,
        oreFe2O3MassFlowKgH: num(n.oreFe2O3MassFlowKgH),
        targetMetallizationPercent: num(n.metallizationPercent),
      };
    case "esfOffgas":
      return {
        kind: "esfOffgas",
        id: n.id,
        label: n.label,
        oreFe2O3MassFlowKgH: num(n.oreFe2O3MassFlowKgH),
        metallizationPercent: num(n.metallizationPercent),
        carbonMassFlowKgH: num(n.carbonMassFlowKgH),
        temperatureK: num(n.temperatureK),
        pressureKpaAbs: gaugeToAbs(n.pressureGauge),
      };
  }
}

export function runFlowsheet(
  state: FlowsheetState,
  unit: PressureUnit,
): ProcessNetworkResult {
  const nodes = state.nodes.map((n) => toProcessNode(n, unit));
  return solveProcessNetwork(nodes);
}

/** 노드를 지우고, 그 노드를 참조하던 곳은 참조를 비운다(끊긴 참조로 남기지 않음). */
export function removeFlowsheetNode(
  state: FlowsheetState,
  id: string,
): FlowsheetState {
  const rest = state.nodes.filter((n) => n.id !== id);
  return {
    nodes: rest.map((n) => ({
      ...n,
      inputId: n.inputId === id ? "" : n.inputId,
      inputIds: n.inputIds.filter((x) => x !== id),
    })),
  };
}
